/**
 * Billing review > All entries, Type: Expenses. Case costs across the firm;
 * admins approve confirmed expenses (and suggestions they've checked) so
 * they export with the time, or move approved ones back.
 */
import { Check, Undo2 } from "lucide-react";
import { useMemo, useState, type FC, type ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";

import { expenseActions } from "../actions";
import {
  EXPENSE_STATUS_LABEL,
  originText,
  payerText
} from "../case/expenseModel";
import { CaseDot } from "../components/CasePicker";
import { useMatters, useReviewExpenses } from "../queries";
import { Segmented } from "../settings/SettingRow";
import type { TimeExpense } from "../types";
import { formatAmount } from "../utils";
import { ALL, FilterSelect } from "./FilterSelect";
import {
  canApproveExpense,
  canUnapproveExpense,
  expenseStatusesFor,
  type ExpenseStatusFilter
} from "./reviewModel";

const shortDate = (iso: string): string => {
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const sum = (rows: TimeExpense[]) => rows.reduce((t, x) => t + x.amount, 0);

type Props = {
  range: { from?: string; to?: string };
  /** The Type switch, shown first in the filter bar. */
  typeSwitch: ReactNode;
};

export const ExpenseReview: FC<Props> = ({ range, typeSwitch }) => {
  const [status, setStatus] = useState<ExpenseStatusFilter>("all");
  const [client, setClient] = useState(ALL);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const filters = useMemo(
    () => ({
      status: expenseStatusesFor(status),
      from: range.from,
      to: range.to,
      client: client === ALL ? undefined : client
    }),
    [status, range.from, range.to, client]
  );
  const query = useReviewExpenses(filters);
  const matters = useMatters();
  const rows = useMemo(() => query.data ?? [], [query.data]);
  const selected = rows.filter((x) => picked.has(x.id));
  const approvable = selected.filter(canApproveExpense);
  const backable = selected.filter(canUnapproveExpense);
  const selectable = rows.filter(
    (x) => canApproveExpense(x) || canUnapproveExpense(x)
  );
  const allPicked =
    selectable.length > 0 && selectable.every((x) => picked.has(x.id));

  const toggle = (id: string, on: boolean) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const run = async (action: Promise<TimeExpense[] | null>) => {
    if (await action) setPicked(new Set());
  };

  const clientOptions = (matters.data ?? []).map((m) => ({
    id: m.individual_id,
    label: m.label
  }));

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="review-expenses">
      <div className="flex flex-wrap items-center gap-1.5 px-4 py-2.5">
        {typeSwitch}
        <FilterSelect
          label="Client"
          value={client}
          options={clientOptions}
          onChange={setClient}
        />
        <span className="flex-1" />
        <Segmented
          label="Expense status"
          value={status}
          onChange={setStatus}
          options={[
            { id: "all", label: "All" },
            { id: "suggested", label: "Suggested" },
            { id: "draft", label: "Confirmed" },
            { id: "approved", label: "Approved" },
            { id: "exported", label: "Exported" }
          ]}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
        {selected.length ? (
          <div
            className="sticky top-0 z-10 mb-2 flex h-10 items-center gap-2.5 rounded-xl bg-foreground pr-1.5 pl-3.5 text-background"
            data-testid="review-expense-bulk-bar"
          >
            <span className="text-base font-medium tabular-nums">
              {selected.length} selected · {formatAmount(sum(selected))}
            </span>
            <span className="flex-1" />
            {backable.length ? (
              <Button
                size="xs"
                variant="ghost"
                className="text-background hover:bg-background/10 hover:text-background"
                onClick={() =>
                  void run(expenseActions.unapprove(backable.map((x) => x.id)))
                }
              >
                <Undo2 /> Unapprove
              </Button>
            ) : null}
            <Button
              size="xs"
              variant="secondary"
              disabled={!approvable.length}
              onClick={() =>
                void run(expenseActions.approve(approvable.map((x) => x.id)))
              }
            >
              <Check /> Approve
              {approvable.length !== selected.length && approvable.length
                ? ` ${approvable.length}`
                : ""}
            </Button>
          </div>
        ) : null}

        {query.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : rows.length ? (
          <div className="shrink-0 overflow-hidden rounded-xl border border-line-2">
            <Table className="table-fixed text-base">
              <colgroup>
                <col className="w-9" />
                <col className="w-16" />
                <col className="w-48" />
                <col />
                <col className="w-14" />
                <col className="w-28" />
                <col className="w-24" />
                <col className="w-24" />
              </colgroup>
              <TableHeader className="bg-fill-1">
                <TableRow>
                  <TableHead className="h-8">
                    <Checkbox
                      aria-label="Select all expenses"
                      checked={allPicked}
                      disabled={!selectable.length}
                      onCheckedChange={(on) =>
                        setPicked(
                          on ? new Set(selectable.map((x) => x.id)) : new Set()
                        )
                      }
                    />
                  </TableHead>
                  <TableHead className="h-8 text-sm">Date</TableHead>
                  <TableHead className="h-8 text-sm">Case</TableHead>
                  <TableHead className="h-8 text-sm">Expense</TableHead>
                  <TableHead className="h-8 text-sm">Code</TableHead>
                  <TableHead className="h-8 text-sm">Who pays</TableHead>
                  <TableHead className="h-8 text-right text-sm">
                    Amount
                  </TableHead>
                  <TableHead className="h-8 text-sm">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((x) => {
                  const canPick =
                    canApproveExpense(x) || canUnapproveExpense(x);
                  const origin = originText(x);
                  return (
                    <TableRow
                      key={x.id}
                      data-testid="review-expense-row"
                      data-state={picked.has(x.id) ? "selected" : undefined}
                      className="h-10.5"
                    >
                      <TableCell className="py-1.5">
                        {canPick ? (
                          <Checkbox
                            aria-label={`Select ${
                              x.description || x.kind_label
                            } on ${x.case_label}`}
                            checked={picked.has(x.id)}
                            onCheckedChange={(on) => toggle(x.id, on === true)}
                          />
                        ) : null}
                      </TableCell>
                      <TableCell className="text-fg-3 tabular-nums">
                        {shortDate(x.date)}
                      </TableCell>
                      <TableCell>
                        <span className="flex min-w-0 items-center gap-1.5">
                          <CaseDot id={x.individual_id} />
                          <span className="truncate">{x.case_label}</span>
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="flex min-w-0 flex-col">
                          <span className="truncate">
                            {x.description || x.kind_label}
                          </span>
                          {origin ? (
                            <span className="truncate text-sm text-fg-3">
                              {origin}
                            </span>
                          ) : null}
                        </span>
                      </TableCell>
                      <TableCell
                        className="font-mono text-sm text-fg-3"
                        title={x.code_label}
                      >
                        {x.utbms_expense}
                      </TableCell>
                      <TableCell className="truncate text-fg-3">
                        {payerText(x) || "Client"}
                      </TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {formatAmount(x.amount)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className="h-5 text-sm font-normal"
                        >
                          {EXPENSE_STATUS_LABEL[x.status]}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <Empty
            className="border border-dashed"
            data-testid="review-expenses-empty"
          >
            <EmptyHeader>
              <EmptyTitle>No expenses</EmptyTitle>
              <EmptyDescription>
                {status === "all" && client === ALL
                  ? "Expenses show up here once they're added on a case's Time pane or suggested from a filed receipt notice."
                  : "No expenses match these filters."}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}
      </div>
    </div>
  );
};
